import { useEffect, useRef, useState } from "react";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

type LoadStatus = "loading" | "ready" | "error";

interface AgreementConsentProps {
	documentUrl: string;
	label: string;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
}

/** 文書の PDF を全ページ表示し、最後までスクロールしてから同意のチェックを入れさせる。 */
export function AgreementConsent({
	documentUrl,
	label,
	checked,
	onCheckedChange,
}: AgreementConsentProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const pagesRef = useRef<HTMLDivElement>(null);
	const endRef = useRef<HTMLDivElement>(null);
	const [status, setStatus] = useState<LoadStatus>("loading");
	// 「戻る」で戻った文書は一度最後まで読んでチェックしているので、読み直させない。
	const [readToEnd, setReadToEnd] = useState(checked);

	useEffect(() => {
		const pages = pagesRef.current;
		if (!pages) return;
		const abort = new AbortController();
		renderPdfPages(documentUrl, pages, abort.signal)
			.then(() => {
				if (!abort.signal.aborted) setStatus("ready");
			})
			.catch((err: unknown) => {
				if (abort.signal.aborted) return;
				console.error("文書の表示に失敗しました", err);
				setStatus("error");
			});
		return () => {
			abort.abort();
			pages.replaceChildren();
		};
	}, [documentUrl]);

	useEffect(() => {
		const root = scrollRef.current;
		const end = endRef.current;
		if (readToEnd || status !== "ready" || !root || !end) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setReadToEnd(true);
					observer.disconnect();
				}
			},
			{ root },
		);
		observer.observe(end);
		return () => observer.disconnect();
	}, [readToEnd, status]);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
			<div
				ref={scrollRef}
				style={{
					height: "min(60vh, 560px)",
					overflowY: "auto",
					scrollbarGutter: "stable",
					padding: 8,
					background: "var(--surface-2)",
					borderRadius: "var(--radius-3)",
					boxShadow: "var(--shadow-ring)",
				}}
			>
				{status === "loading" && <p style={messageStyle}>読み込んでいます…</p>}
				{status === "error" && (
					<p style={{ ...messageStyle, color: "var(--danger)" }}>
						読み込めませんでした。ページを再読み込みしてください。
					</p>
				)}
				<div ref={pagesRef} style={{ display: "flex", flexDirection: "column", gap: 8 }} />
				<div ref={endRef} style={{ height: 1 }} />
			</div>

			<label
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					fontSize: "var(--text-ui)",
					color: readToEnd ? "var(--fg-1)" : "var(--fg-3)",
					cursor: readToEnd ? "pointer" : "not-allowed",
					userSelect: "none",
				}}
			>
				<input
					type="checkbox"
					checked={checked}
					disabled={!readToEnd}
					onChange={(e) => onCheckedChange(e.target.checked)}
					data-testid="terms-consent-checkbox"
					style={{
						width: 14,
						height: 14,
						margin: 0,
						accentColor: "var(--accent-solid)",
						cursor: "inherit",
					}}
				/>
				{label}
			</label>
			{/* 読み終えても場所は残し、カードの高さを変えない。 */}
			<p
				style={{
					margin: 0,
					fontSize: "var(--text-caption)",
					color: "var(--fg-3)",
					lineHeight: "var(--leading-ui)",
					visibility: readToEnd ? "hidden" : "visible",
				}}
			>
				最後までスクロールすると同意できます
			</p>
		</div>
	);
}

const messageStyle: React.CSSProperties = {
	margin: 0,
	padding: 8,
	fontSize: "var(--text-ui)",
	color: "var(--fg-2)",
	lineHeight: "var(--leading-ui)",
};

/** 各ページを `container` の横幅に合わせ、画面の画素密度で canvas に描画して追加する。 */
async function renderPdfPages(
	url: string,
	container: HTMLElement,
	signal: AbortSignal,
): Promise<void> {
	const pdfjs = await import("pdfjs-dist");
	if (signal.aborted) return;
	pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
	const loadingTask = pdfjs.getDocument({ url });
	signal.addEventListener(
		"abort",
		() => {
			loadingTask.destroy().catch((err: unknown) => {
				console.error("文書の読み込みの中断に失敗しました", err);
			});
		},
		{ once: true },
	);
	const pdf = await loadingTask.promise;
	const cssWidth = container.clientWidth;
	const pixelRatio = window.devicePixelRatio || 1;
	for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
		const page = await pdf.getPage(pageNumber);
		const scale = (cssWidth / page.getViewport({ scale: 1 }).width) * pixelRatio;
		const viewport = page.getViewport({ scale });
		const canvas = document.createElement("canvas");
		canvas.width = Math.floor(viewport.width);
		canvas.height = Math.floor(viewport.height);
		canvas.style.display = "block";
		canvas.style.width = "100%";
		canvas.style.boxShadow = "var(--shadow-ring)";
		await page.render({ canvas, viewport }).promise;
		if (signal.aborted) return;
		container.append(canvas);
	}
}
